import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { writeActiveDashboardId } from './components/dashboard/dashboardCache';
import SymbolDetailPage from './pages/SymbolDetailPage';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import Dashboard from './components/Dashboard';
import InputForm from './components/InputForm';
import UpgradeModal from './components/UpgradeModal';
import BulkRiskCalculator from './components/BulkRiskCalculator';
import CsvUpload from './components/CsvUpload';
import Footer from './components/Footer';
import TraderPsychologyTipBanner from './components/TraderPsychologyTipBanner';
import BrokerKeyRotationBanner from './components/BrokerKeyRotationBanner';
import Sidebar from './components/Sidebar';
import MobileBottomNav from './components/MobileBottomNav';
import PageHeading from './components/PageHeading';
import DisclaimerModal from './components/DisclaimerModal';
import { acceptDisclaimer, shouldShowDisclaimer } from './utils/disclaimerAcceptance';
import RiskAssessmentProLock from './components/RiskAssessmentProLock';
import TutorialModal from './components/TutorialModal';
import GuidedTour from './components/GuidedTour';
import GoogleSignIn from './components/GoogleSignIn';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ClerkSessionBridge from './components/ClerkSessionBridge';
import AdminEntities from './pages/AdminEntities';
import { ENTITY_CHANGED_EVENT, getActiveEntitySlug, getActiveEntityBrand } from './utils/activeEntity';
import { brandDefaultMode } from './utils/brandTheme';
import { setStrategyLabels } from './utils/strategyLabels';
import { useAppAutoRefresh } from './hooks/useAppAutoRefresh';
import { useDeployUpdateCheck } from './hooks/useDeployUpdateCheck';
import { useMobileHeaderAutoHide } from './hooks/useMobileHeaderAutoHide';
import { adminPagesBlockedInPreview, adminViewLabel, HIDDEN_ADMIN_PAGES } from './utils/adminViewAs';
import FAQ from './pages/FAQ';
import HowWeCalculate from './pages/HowWeCalculate';
import Apex30PercentRule from './pages/Apex30PercentRule';
import HowToExportCsv from './pages/HowToExportCsv';
import Account from './pages/Account';
import AdminFeedback from './pages/AdminFeedback';
import AdminVectorNtMappings from './pages/AdminVectorNtMappings';
import AdminScenarioData from './pages/AdminScenarioData';
import { SHEETS_CHANGED_EVENT, OPEN_ARRANGE_KEY } from './components/results/ArrangeResultsTabs';
import AdminStrategySubmissionReview from './pages/AdminStrategySubmissionReview';
import AdminUsers from './pages/AdminUsers';
import AdminLogs from './pages/AdminLogs';
import AdminMetricsHistory from './pages/AdminMetricsHistory';
import AdminSiteMetrics from './pages/AdminSiteMetrics';
import useActivityPing from './hooks/useActivityPing';
import AdminAlerts from './pages/AdminAlerts';
import TradeWatcher, { TradeWatcherProLock } from './pages/TradeWatcher';
import BrandOnboarding, { BRAND_INVITE_RETURN_KEY } from './pages/BrandOnboarding';
import useFeatureAccess, { featureForPage } from './hooks/useFeatureAccess';
import LockedFeaturePreview from './components/LockedFeaturePreview';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Security from './pages/Security';
import DesktopAppGuide from './pages/DesktopAppGuide';
import Tutorials from './pages/Tutorials';
import ResultsDashboard from './pages/ResultsDashboard';
import VectorCapitalPage from './pages/VectorCapitalPage';
import VectorCapitalPlusPage from './pages/VectorCapitalPlusPage';
import PortfolioSimulatorPage from './pages/PortfolioSimulatorPage';
import MySimulatorPage from './pages/personal/MySimulatorPage';
import PortfolioRiskPage from './pages/portfolioRisk/PortfolioRiskPage';
import AskRiskloLauncher from './components/askRisklo/AskRiskloLauncher';
import { ASK_PAGES } from './components/askRisklo/renderAnswer';
import { applyBrandIdentity, getBrandIdentity, loadVanityBrand, useBrandIdentity } from './utils/brandIdentity';
import { vanitySlugFromPage } from './utils/appPages';
import AdminAiAssistant from './pages/AdminAiAssistant';
import { useWorkspace } from './utils/workspace';
import HedgingRiskPage from './pages/HedgingRiskPage';
import RiskToolsPage from './pages/RiskToolsPage';
import { hasClientReportingAccess } from './utils/tierAccess';
import ResultsSpreadsheetPage from './pages/ResultsSpreadsheetPage';
import DashboardPage from './pages/DashboardPage';
import PropFirmPlannerPage from './pages/PropFirmPlannerPage';
import VectorDashboardPage from './pages/VectorDashboardPage';
import Login from './pages/Login';
import VectorUpdateLog from './pages/VectorUpdateLog';
import MarketingLayout from './marketing/MarketingLayout';
import LandingPage from './marketing/LandingPage';
import FeaturesPage from './marketing/FeaturesPage';
import CompaniesPage from './marketing/CompaniesPage';
import BrandStrategies from './pages/BrandStrategies';
import { prepareRetiredPageRedirect } from './utils/strategiesPageLinks';
import BrandSettings from './pages/BrandSettings';
import PricingPage from './marketing/PricingPage';
import AboutPage from './marketing/AboutPage';
import ContactPage from './marketing/ContactPage';
import HelpPage from './marketing/HelpPage';
import { API_ENDPOINTS } from './config';
import { getBearerAuthHeaders, getVectorApiHeaders } from './utils/authFetchHeaders';
import { sheetsAudienceCacheKey } from './utils/adminViewAs';
import { IconAlert, IconChart } from './components/Icons';
import { clearAnalyticsUser, initAnalytics, setAnalyticsUser, trackEvent, trackPageView } from './utils/analytics';
import { getRiskloAuthToken, getStoredRiskloUserEmail } from './utils/storedSession';
import { DEFAULT_ACCOUNT_SIZE, DEFAULT_THRESHOLD } from './utils/accountSizes';
import {
  MULTI_STRAT_SHEET_NAME,
  LEGACY_MULTI_STRAT_NAMES,
} from './utils/vectorCapitalScenario';
import { isMembershipAccessDenied, membershipDeniedMessage as parseMembershipDeniedMessage } from './utils/userFacingStrategyErrors';

/** Matches server `verifyWhopMembership` sample bypass — always allowed without Vector sheet access */
const SAMPLE_STRATEGY_NAME = 'Sample Strategy (Demo)';

function createDefaultBulkInputRow(riskMode, consistencyPercent) {
  return {
    id: Date.now(),
    accountName: '',
    strategy: '',
    contractType: 'MNQ',
    consistencyPercent: Number(consistencyPercent) || 50,
    accountSize: riskMode === 'apexMae' ? DEFAULT_ACCOUNT_SIZE : '',
    contracts: '',
    maxDrawdown: '',
    currentBalance: '',
    startOfDayProfit: '',
    safetyNet: riskMode === 'apexMae' ? DEFAULT_THRESHOLD : '',
  };
}

function normalizeStoredBulkInputRows(parsed, riskMode, defaultPct) {
  const template = createDefaultBulkInputRow(riskMode, defaultPct);
  return parsed.slice(0, 40).map((row, i) => ({
    ...template,
    ...row,
    id: row.id != null && row.id !== '' ? row.id : Date.now() + i,
    consistencyPercent: Number(row.consistencyPercent) || defaultPct,
  }));
}

function vectorSheetAccessDenied(response, data) {
  return isMembershipAccessDenied(response, data);
}

/**
 * Netlify / build-time: set REACT_APP_SITE_STATUS_MESSAGE to show a global notice.
 * Use "DEFAULT" (any case) or leave unset/empty to hide. Any other text is shown site-wide.
 */
function getSiteStatusBroadcastMessage() {
  const raw = process.env.REACT_APP_SITE_STATUS_MESSAGE;
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed || trimmed.toUpperCase() === 'DEFAULT') return null;
  return trimmed;
}

function AppContent() {
  const FORM_INPUTS_STORAGE_KEY = 'risklo_last_inputs_by_mode';
  const {
    user,
    isPro,
    isMax,
    isVectorAdmin,
    isBeta,
    isDevMode,
    isAdmin,
    isVectorAdminPlus,
    isSupport,
    isSales,
    isAdminActual,
    adminViewAs,
    isAdminViewPreview,
    isSelfUsePreview,
    loading: authLoading,
    tierReady,
    handleGoogleSignIn,
    refreshSession,
  } = useAuth();
  const {
    canSee: canSeeFeature,
    isLocked: isFeatureLocked,
    brand: featureBrand,
    loaded: featureAccessLoaded,
    isGlobalOn,
    isRemovedForBrand,
  } = useFeatureAccess();
  // Admin → Brands → RiskLo-wide settings → Intro tour. Off = no pop-up and no
  // "Take the tour" button, for every brand and user.
  const canSeeIntroTour = isGlobalOn('intro_tour');
  const canSeeTradeWatcher = canSeeFeature('trade_watcher');
  const location = useLocation();
  const navigate = useNavigate();
  const skipNextPathScrollRef = useRef(true);

  // Long-lived tabs: soft-refresh session + broadcast data reload every 30 min;
  // full reload after 8 h; refresh when returning after 5+ min in background.
  const handleAppAutoRefresh = useCallback(() => {
    refreshSession();
  }, [refreshSession]);
  useAppAutoRefresh({ onRefresh: handleAppAutoRefresh });
  const { updateReady: deployUpdateReady, reloadNow: reloadForDeploy } = useDeployUpdateCheck();
  
  // Map URL paths to page names for backward compatibility
  const getPageFromPath = (pathname) => {
    const path = (pathname || '').replace(/^\/+/, '').replace(/\/+$/, '') || 'home';
    return path || 'home';
  };
  
  const currentPage = getPageFromPath(location.pathname);
  const brandIdentity = useBrandIdentity();

  // Partner short links (risklo.io/vector): pick that brand, then its sign-in or Overview.
  const vanitySlug = vanitySlugFromPage(currentPage);
  const [vanityChecking, setVanityChecking] = useState(Boolean(vanitySlug));
  useEffect(() => {
    if (!vanitySlug) { setVanityChecking(false); return undefined; }
    let alive = true;
    setVanityChecking(true);
    loadVanityBrand(vanitySlug).then((found) => {
      if (!alive) return;
      setVanityChecking(false);
      // A brand's link → its sign-in (or Overview); anything else unknown → home.
      if (found) navigate(localStorage.getItem('risklo_auth_token') ? '/dashboard' : '/login', { replace: true });
      else navigate('/', { replace: true });
    });
    return () => { alive = false; };
  }, [vanitySlug, navigate]);

  // Browser tab: "Vector · Overview" for brand-first plans.
  useEffect(() => {
    const label = ASK_PAGES[currentPage] || null;
    applyBrandIdentity(getBrandIdentity(), label);
  }, [currentPage, brandIdentity]);

  // Activity for the Full Admin metrics page: a ping a minute while someone is using RiskLo.
  useActivityPing(currentPage, Boolean(user));

  const isMarketingRoute = useMemo(() => {
    const marketingPages = new Set(['features', 'pricing', 'companies', 'contact', 'help']);
    return marketingPages.has(currentPage)
      || (currentPage === 'home' && !user && !authLoading);
  }, [currentPage, user, authLoading]);

  useEffect(() => {
    initAnalytics();
  }, []);
  
  // Navigation handler that updates URL
  const setCurrentPage = useCallback((page) => {
    const path = page === 'home' ? '/' : `/${page}`;
    navigate(path);
  }, [navigate]);

  // The RiskLo title / logo: Overview, on the user's default dashboard (not
  // whichever one they had open last). Signed out, or without Overview: home.
  const goHome = useCallback(() => {
    if (!user?.email || currentPage === 'login' || !canSeeFeature('overview')) {
      navigate('/');
      return;
    }
    writeActiveDashboardId(user.email, null);
    navigate('/dashboard', { state: { openDefaultDashboard: Date.now() } });
  }, [user?.email, currentPage, canSeeFeature, navigate]);

  useEffect(() => {
    if (HIDDEN_ADMIN_PAGES.includes(currentPage)) {
      setCurrentPage('home');
      return;
    }
    if (!isAdminActual) return;
    const blocked = adminPagesBlockedInPreview(adminViewAs);
    if (!blocked.includes(currentPage)) return;
    setCurrentPage('account');
  }, [isAdminActual, adminViewAs, currentPage, setCurrentPage]);

  // First visit to the root URL ("/") in this browser tab — if the user is
  // signed in, send them straight to the Dashboard instead of the
  // RiskTools home. Marked done in sessionStorage so subsequent navigations
  // to "/" (e.g. clicking "Blowout Risk" in the sidebar) work normally and
  // don't redirect them away. Signed-out visitors still see the marketing
  // LandingPage on the root URL.
  // Legacy URL: /vector-dashboard → /client-reporting (broker reporting only).
  // Removed page: /overview-test → /dashboard.
  useEffect(() => {
    if (location.pathname === '/vector-dashboard' || location.pathname === '/vector-dashboard/') {
      navigate('/client-reporting', { replace: true });
      return;
    }
    if (location.pathname === '/overview-test' || location.pathname === '/overview-test/') {
      navigate('/dashboard', { replace: true });
      return;
    }
    // Legacy URL: /vector-updates → /updates (brand-neutral).
    if (location.pathname === '/vector-updates' || location.pathname === '/vector-updates/') {
      navigate('/updates', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (authLoading || !tierReady) return;
    if (currentPage !== 'vector-capital' && currentPage !== 'vector-capital-plus') return;
    if (isAdmin) return;
    navigate('/dashboard', { replace: true });
  }, [authLoading, tierReady, currentPage, isAdmin, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.email) return;
    if (location.pathname !== '/') return;
    if (typeof sessionStorage === 'undefined') return;
    if (sessionStorage.getItem('risklo_root_dashboard_redirect') === 'done') return;
    sessionStorage.setItem('risklo_root_dashboard_redirect', 'done');
    navigate('/dashboard', { replace: true });
  }, [authLoading, user?.email, location.pathname, navigate]);

  // Someone who pressed "Sign in" on a brand invite goes back to it once,
  // wherever sign-in dropped them.
  useEffect(() => {
    if (authLoading || !user?.email) return;
    if (currentPage === 'brand-onboarding' || currentPage === 'login') return;
    // Let the OAuth callback finish its own redirect first; this runs again after.
    if (new URLSearchParams(location.search).get('signIn')) return;
    let pending = false;
    try {
      pending = sessionStorage.getItem(BRAND_INVITE_RETURN_KEY) === '1';
      sessionStorage.removeItem(BRAND_INVITE_RETURN_KEY);
    } catch { /* ignore */ }
    if (pending) navigate('/brand-onboarding', { replace: true });
  }, [authLoading, user?.email, currentPage, location.search, navigate]);

  const goToAccountEmailSummaries = useCallback(() => {
    navigate('/account#email-summaries');
  }, [navigate]);
  
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalMessage, setUpgradeModalMessage] = useState(null);
  const openUpgradeModal = useCallback((customMessage) => {
    const message = typeof customMessage === 'string' && customMessage.trim()
      ? customMessage.trim()
      : null;
    setUpgradeModalMessage(message);
    setShowUpgradeModal(true);
  }, []);
  // Hidden until the acceptance check says otherwise (utils/disclaimerAcceptance.js).
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  // The email whose acceptance check has finished (the guided tour waits for it).
  const [disclaimerCheckedFor, setDisclaimerCheckedFor] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  // Mobile sidebar drawer (sidebar is permanent on desktop and ignores this flag).
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Desktop-only: collapse the sidebar to an icons-only rail to free page width.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('risklo_sidebar_collapsed') === 'true',
  );
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('risklo_sidebar_collapsed', String(next)); } catch (_e) { /* ignore */ }
      return next;
    });
  }, []);
  const [riskMode, setRiskMode] = useState(() => {
    return localStorage.getItem('riskMode') || 'risk'; // 'risk' or 'apexMae'
  });
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'light' || saved === 'dark') return saved;
      // First visit: the brand's default (Admin → Brands → Layout), else dark.
      return brandDefaultMode(getActiveEntityBrand()) || 'dark';
    } catch (_err) {
      return 'dark';
    }
  });
  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);
  const [riskMetrics, setRiskMetrics] = useState(null); // Metrics for Risk mode
  const [apexMaeMetrics, setApexMaeMetrics] = useState(null); // Metrics for % Rule mode
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('activeHomeTab');
    return saved || 'single';
  }); // 'single', 'bulk', or 'csv'
  const [lastFormData, setLastFormData] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(FORM_INPUTS_STORAGE_KEY) || '{}');
      return stored?.[localStorage.getItem('riskMode') || 'risk'] || null;
    } catch (_err) {
      return null;
    }
  }); // Store last form data for calculations
  const [lastVisitedTab, setLastVisitedTab] = useState(() => {
    return localStorage.getItem('lastVisitedTab') || 'home';
  }); // Track last visited main tab
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // baseSheetNames = real Vector strategies plus the synthetic Vector Master
  // Strat (no custom combos). sheetNames (derived below) prepends the user's
  // saved combos onto this so risk-page consumers see one canonical list.
  const [baseSheetNames, setBaseSheetNames] = useState([]);
  // Internal (admin-only) strategy names from /api/sheets — used to ring
  // Vector Results tabs green for admins. Members receive an empty list.
  const [internalSheetNames, setInternalSheetNames] = useState([]);
  // Strategy Settings renames: { "<data name>": "<display name>" }. Tabs stay
  // keyed by the data name so data fetches keep working; only the label moves.
  const [sheetLabels, setSheetLabels] = useState({});
  // Switching brand changes which strategies exist, their labels, dashboards and
  // tier scoping. Rather than invalidate each cache individually, clear the
  // cached sheet lists and reload — a brand switch is rare and deliberate.
  useEffect(() => {
    const onEntityChanged = () => {
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith('risklo_sheet_names_'))
          .forEach((k) => localStorage.removeItem(k));
      } catch (_e) { /* private browsing */ }
      window.location.reload();
    };
    window.addEventListener(ENTITY_CHANGED_EVENT, onEntityChanged);
    return () => window.removeEventListener(ENTITY_CHANGED_EVENT, onEntityChanged);
  }, []);
  // Pro+ user's saved Custom Multi-Strat combos. Exposed app-wide so risk
  // pages (Blowout Risk, % Rule Risk, Bulk Risk, CSV Upload) and dashboard
  // widget configs can offer them as strategy options. Empty for Basic.
  const [customCombos, setCustomCombos] = useState([]);
  const [customCombosLoaded, setCustomCombosLoaded] = useState(false);

  const upsertCustomCombo = useCallback((combo) => {
    if (!combo?.id) return;
    setCustomCombos((prev) => {
      const next = [...prev];
      const idx = next.findIndex((c) => c.id === combo.id);
      if (idx >= 0) next[idx] = combo;
      else next.unshift(combo);
      return next;
    });
  }, []);

  const removeCustomCombo = useCallback((comboId) => {
    if (!comboId) return;
    setCustomCombos((prev) => prev.filter((c) => c.id !== comboId));
  }, []);

  useEffect(() => {
    if (!tierReady) return undefined;
    if (!user?.email || !isPro) {
      setCustomCombos([]);
      setCustomCombosLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setCustomCombosLoaded(false);
    (async () => {
      try {
        const headers = getBearerAuthHeaders();
        const resp = await fetch(API_ENDPOINTS.customMultistrats, { headers });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (resp.ok && data?.success && Array.isArray(data.combos)) {
          setCustomCombos(data.combos);
        } else {
          setCustomCombos([]);
        }
      } catch (_err) {
        if (!cancelled) setCustomCombos([]);
      } finally {
        if (!cancelled) setCustomCombosLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [tierReady, user?.email, isPro]);

  // Derived strategy list passed to every consumer (Blowout Risk, % Rule,
  // Bulk Risk, CSV Upload, Dashboard widget config). Combos appear first as
  // `custom:<id>` strings; consumers resolve display names locally and the
  // server-side analyze / strategy-sheet routes detect the prefix to fetch
  // the combined rows.
  const sheetNames = React.useMemo(() => {
    const comboValues = (customCombos || []).map((c) => `custom:${c.id}`);
    return [...comboValues, ...(baseSheetNames || [])];
  }, [customCombos, baseSheetNames]);
  const [loadingSheets, setLoadingSheets] = useState(true);
  /**
   * True only when the API actually refused us (403 + requiresMembership).
   *
   * This used to be inferred from "are there any strategies besides the demo
   * sample", which conflated two very different states. For Vector they
   * happened to coincide — no membership meant the API denied us, which meant
   * no strategies. For a brand that simply has nothing in Strategy Data yet,
   * it produced a false "Access denied" on a page the user has every right to
   * see. Emptiness is not a permission problem.
   */
  const [membershipDenied, setMembershipDenied] = useState(false);
  const [membershipDeniedText, setMembershipDeniedText] = useState('');
  const hasStrategyData = React.useMemo(
    () => (baseSheetNames || []).some((name) => name && name !== SAMPLE_STRATEGY_NAME),
    [baseSheetNames],
  );
  // Self users (no company) get Results and the Simulator built from their own
  // accounts and strategies. Platform staff keep the company views unless
  // they're previewing Self-use.
  const workspace = useWorkspace(Boolean(user?.email));
  const personalViews = isSelfUsePreview || (workspace.selfUser && !workspace.isFullAdmin && !isAdmin);
  // Results is hidden for self users for now: send /results back to Overview.
  useEffect(() => {
    if (personalViews && currentPage === 'results') setCurrentPage('home');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalViews, currentPage]);
  // Sheet Manager is gone: its tools live in Results → Arrange tabs. Old links
  // and bookmarks land on Results with that panel open.
  useEffect(() => {
    if (currentPage !== 'admin-sheet-manager') return;
    try { window.sessionStorage.setItem(OPEN_ARRANGE_KEY, '1'); } catch (_e) { /* opens from the button instead */ }
    navigate('/results', { replace: true });
  }, [currentPage, navigate]);
  // Create strategy, New live strategy, Equity Strategies and Webhooks moved
  // into the Strategies page: their old links open it where that tool now is.
  useEffect(() => {
    if (prepareRetiredPageRedirect(currentPage)) navigate('/brand-strategies', { replace: true });
  }, [currentPage, navigate]);
  // Admin → Trade Watch is now Trade Watcher → Alerts.
  useEffect(() => {
    if (currentPage === 'admin-trade-watch') navigate('/trade-watcher?tab=alerts', { replace: true });
  }, [currentPage, navigate]);
  const hasVectorAccess = React.useMemo(() => {
    // Self-use preview: someone with no company has no company strategies.
    if (isSelfUsePreview) return false;
    if (loadingSheets) return null;
    return !membershipDenied;
  }, [loadingSheets, membershipDenied, isSelfUsePreview]);
  const shownMembershipDeniedText = isSelfUsePreview ? '' : membershipDeniedText;
  const pickerShownRef = useRef(false); // prevent duplicate OAuth callback handling
  
  // State to store CSV-generated rows for bulk calculator
  const [csvBulkRows, setCsvBulkRows] = useState(null);
  const [csvFileNames, setCsvFileNames] = useState(null);
  
  // State to store bulk calculator results (persists across navigation)
  const [bulkResultsRisk, setBulkResultsRisk] = useState(null);
  const [bulkResultsApexMae, setBulkResultsApexMae] = useState(null);
  /** Table row inputs for bulk calculator (persisted per user like results). */
  const [bulkInputRows, setBulkInputRows] = useState(null);
  const [bulkStateHydrated, setBulkStateHydrated] = useState(false);
  const [consistencyPercent, setConsistencyPercent] = useState(50);
  const SHEET_NAMES_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  /** Shorter TTL so a newly granted Vector membership shows up without a long wait */
  const SHEET_NAMES_NO_VECTOR_CACHE_TTL_MS = 2 * 60 * 1000;
  const SHEET_NAMES_429_COOLDOWN_MS = 60 * 1000; // 1 minute

  const getBulkStorageKey = useCallback(
    (suffix) => `risklo_bulk_${suffix}_${user?.email || 'anon'}`,
    [user?.email]
  );
  
  // Get current metrics based on mode
  const metrics = riskMode === 'risk' ? riskMetrics : apexMaeMetrics;

  // Scroll to top on client-side navigations only — skip the first run (full reload / hard refresh)
  // so we don't fight the browser or cause a tiny scroll "jitter" when scroll is already 0.
  useEffect(() => {
    if (skipNextPathScrollRef.current) {
      skipNextPathScrollRef.current = false;
      return;
    }
    // Deep links (e.g. /account#email-summaries) should not be overridden by scroll-to-top
    if (location.hash) {
      return;
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  // Track last visited tab whenever currentPage changes to home or results
  useEffect(() => {
    if (currentPage === 'home' || currentPage === 'results') {
      setLastVisitedTab(currentPage);
      localStorage.setItem('lastVisitedTab', currentPage);
    }
  }, [currentPage]);

  // Persist risk mode
  useEffect(() => {
    localStorage.setItem('riskMode', riskMode);
  }, [riskMode]);

  // Hydrate last analyzed input set for the active risk mode.
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(FORM_INPUTS_STORAGE_KEY) || '{}');
      setLastFormData(stored?.[riskMode] || null);
    } catch (_err) {
      setLastFormData(null);
    }
  }, [riskMode]);

  // Persist home sub-tab so refresh keeps single/bulk/csv position.
  useEffect(() => {
    localStorage.setItem('activeHomeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    const storageKey = `risklo_selected_consistency_percent_${user?.email || 'anon'}`;
    const saved = Number(localStorage.getItem(storageKey));
    const fromProfile = Number(user?.defaultConsistencyPercent);
    const resolved = [20, 30, 40, 50, 55, 60].includes(saved)
      ? saved
      : ([20, 30, 40, 50, 55, 60].includes(fromProfile) ? fromProfile : 50);
    setConsistencyPercent(resolved);
  }, [user?.email, user?.defaultConsistencyPercent]);

  useEffect(() => {
    const storageKey = `risklo_selected_consistency_percent_${user?.email || 'anon'}`;
    localStorage.setItem(storageKey, String(consistencyPercent));
  }, [consistencyPercent, user?.email]);

  // Hydrate bulk CSV/risk state from localStorage (per user) so refresh keeps results.
  useEffect(() => {
    const safeParse = (value) => {
      if (!value) return null;
      try {
        return JSON.parse(value);
      } catch (err) {
        return null;
      }
    };

    const pctKey = `risklo_selected_consistency_percent_${user?.email || 'anon'}`;
    const savedPct = Number(localStorage.getItem(pctKey));
    const defaultPct = [20, 30, 40, 50, 55, 60].includes(savedPct) ? savedPct : 50;

    setCsvBulkRows(safeParse(localStorage.getItem(getBulkStorageKey('csv_rows'))));
    setCsvFileNames(safeParse(localStorage.getItem(getBulkStorageKey('csv_file_names'))));
    setBulkResultsRisk(safeParse(localStorage.getItem(getBulkStorageKey('results_risk'))));
    setBulkResultsApexMae(safeParse(localStorage.getItem(getBulkStorageKey('results_apex_mae'))));

    const inputRaw = localStorage.getItem(getBulkStorageKey('input_rows'));
    const parsedInput = inputRaw ? safeParse(inputRaw) : null;
    if (Array.isArray(parsedInput) && parsedInput.length > 0) {
      setBulkInputRows(normalizeStoredBulkInputRows(parsedInput, riskMode, defaultPct));
    } else {
      setBulkInputRows([createDefaultBulkInputRow(riskMode, defaultPct)]);
    }

    setBulkStateHydrated(true);
    // Intentionally depend only on getBulkStorageKey so toggling Blowout / % Rule does not reload rows from disk over in-memory edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- riskMode read at hydrate time per session key
  }, [getBulkStorageKey]);

  useEffect(() => {
    if (!bulkStateHydrated) return;
    const key = getBulkStorageKey('csv_rows');
    if (csvBulkRows && Array.isArray(csvBulkRows) && csvBulkRows.length > 0) {
      localStorage.setItem(key, JSON.stringify(csvBulkRows));
    } else {
      localStorage.removeItem(key);
    }
  }, [csvBulkRows, bulkStateHydrated, getBulkStorageKey]);

  useEffect(() => {
    if (!bulkStateHydrated) return;
    const key = getBulkStorageKey('csv_file_names');
    if (csvFileNames && typeof csvFileNames === 'object') {
      localStorage.setItem(key, JSON.stringify(csvFileNames));
    } else {
      localStorage.removeItem(key);
    }
  }, [csvFileNames, bulkStateHydrated, getBulkStorageKey]);

  useEffect(() => {
    if (!bulkStateHydrated) return;
    const key = getBulkStorageKey('results_risk');
    if (bulkResultsRisk && Array.isArray(bulkResultsRisk) && bulkResultsRisk.length > 0) {
      localStorage.setItem(key, JSON.stringify(bulkResultsRisk));
    } else {
      localStorage.removeItem(key);
    }
  }, [bulkResultsRisk, bulkStateHydrated, getBulkStorageKey]);

  useEffect(() => {
    if (!bulkStateHydrated) return;
    const key = getBulkStorageKey('results_apex_mae');
    if (bulkResultsApexMae && Array.isArray(bulkResultsApexMae) && bulkResultsApexMae.length > 0) {
      localStorage.setItem(key, JSON.stringify(bulkResultsApexMae));
    } else {
      localStorage.removeItem(key);
    }
  }, [bulkResultsApexMae, bulkStateHydrated, getBulkStorageKey]);

  useEffect(() => {
    if (!bulkStateHydrated || bulkInputRows === null) return;
    const key = getBulkStorageKey('input_rows');
    localStorage.setItem(key, JSON.stringify(bulkInputRows));
  }, [bulkInputRows, bulkStateHydrated, getBulkStorageKey]);

  /** Clear persisted bulk inputs, CSV staging, results, and reset to one empty row (current risk mode + % rule). */
  const resetBulkToDefault = useCallback(() => {
    if (!bulkStateHydrated) return;
    try {
      localStorage.removeItem(getBulkStorageKey('csv_rows'));
      localStorage.removeItem(getBulkStorageKey('csv_file_names'));
      localStorage.removeItem(getBulkStorageKey('results_risk'));
      localStorage.removeItem(getBulkStorageKey('results_apex_mae'));
      localStorage.removeItem(getBulkStorageKey('input_rows'));
    } catch (_err) {
      /* ignore */
    }
    setCsvBulkRows(null);
    setCsvFileNames(null);
    setBulkResultsRisk(null);
    setBulkResultsApexMae(null);
    setBulkInputRows([createDefaultBulkInputRow(riskMode, consistencyPercent)]);
  }, [bulkStateHydrated, consistencyPercent, getBulkStorageKey, riskMode]);

  // Apply theme to body — marketing pages always render dark.
  useEffect(() => {
    const root = document.body;
    const applied = isMarketingRoute ? 'dark' : theme;
    root.classList.remove('light-mode', 'dark-mode');
    root.classList.add(`${applied}-mode`);
    document.documentElement.classList.remove('light-mode', 'dark-mode');
    document.documentElement.classList.add(`${applied}-mode`);
    if (!isMarketingRoute) {
      try {
        localStorage.setItem('theme', theme);
      } catch (_err) {
        // ignore
      }
    }
  }, [theme, isMarketingRoute]);

  useEffect(() => {
    if (!user) {
      clearAnalyticsUser();
      return;
    }

    const planType = isAdmin
      ? 'admin'
      : isMax
        ? 'max'
        : isBeta
          ? 'beta'
          : isPro
            ? 'pro'
            : 'basic';

    setAnalyticsUser(user, { planType });
  }, [user, isAdmin, isMax, isBeta, isPro]);

  useEffect(() => {
    trackPageView(location.pathname, document.title, {
      page_name: currentPage,
      is_mobile: window.innerWidth <= 768,
    });
  }, [location.pathname, currentPage]);

  // Allow components like Footer to navigate via a simple global event
  useEffect(() => {
    const handler = (e) => {
      const page = e?.detail;
      if (typeof page === 'string' && page.length > 0) {
        setCurrentPage(page);
      }
    };
    window.addEventListener('navigate', handler);
    return () => window.removeEventListener('navigate', handler);
  }, [setCurrentPage]);

  // Allow components to trigger the guided tour
  useEffect(() => {
    const handler = () => {
      setShowGuidedTour(true);
    };
    window.addEventListener('showGuidedTour', handler);
    return () => window.removeEventListener('showGuidedTour', handler);
  }, []);

  // Disclaimer: signed-in users only, once per account for good. The server
  // remembers the acceptance, so another device, browser or brand domain
  // never asks again.
  const userEmail = user?.email ? String(user.email).trim() : '';
  useEffect(() => {
    if (!userEmail) {
      setShowDisclaimer(false);
      return undefined;
    }
    let cancelled = false;
    shouldShowDisclaimer(userEmail).then((show) => {
      if (cancelled) return;
      setShowDisclaimer(show);
      setDisclaimerCheckedFor(userEmail);
    });
    return () => { cancelled = true; };
  }, [userEmail]);

  // Check if guided tour should be shown (after disclaimer is accepted).
  //
  // Bug history: this used to re-fire the tour on every login when a user
  // had completed it as a guest (writes `tour_completed`) or when their email
  // casing varied across sessions (writes `tour_completed_Foo@Bar.com` but
  // reads `tour_completed_foo@bar.com`). Now we:
  //   1. Normalize the email key to lowercase/trim on both read and write.
  //   2. Treat the generic `tour_completed` flag as also satisfying the
  //      per-user check — so anyone who's ever dismissed the tour is done.
  //   3. Promote the generic flag to a per-email flag the first time the user
  //      shows up signed in, so the legacy state migrates forward.
  useEffect(() => {
    if (authLoading) return;
    if (!user?.email) return;
    if (currentPage === 'login') return;
    if (showDisclaimer || disclaimerCheckedFor !== String(user.email).trim()) return;
    // Wait for the feature switches so a disabled tour never flashes open.
    if (!featureAccessLoaded || !canSeeIntroTour) return;

    const genericCompleted = localStorage.getItem('tour_completed') === 'true';
    const normalizedEmail = String(user.email).trim().toLowerCase();
    const perEmailKey = `tour_completed_${normalizedEmail}`;
    const perEmailCompleted = localStorage.getItem(perEmailKey) === 'true';

    if (genericCompleted && !perEmailCompleted) {
      localStorage.setItem(perEmailKey, 'true');
    }

    const tourCompleted = genericCompleted || perEmailCompleted;
    if (!tourCompleted) {
      const timerId = setTimeout(() => {
        setShowGuidedTour(true);
      }, 500);
      return () => clearTimeout(timerId);
    }
    return undefined;
  }, [showDisclaimer, disclaimerCheckedFor, user, authLoading, currentPage, featureAccessLoaded, canSeeIntroTour]);

  const fetchSheetNames = useCallback(async () => {
    if (authLoading) {
      return;
    }

    const token = getRiskloAuthToken();
    const emailForSheets =
      (user?.email && String(user.email).trim()) || getStoredRiskloUserEmail();

    if (token && !emailForSheets) {
      setLoadingSheets(true);
      return;
    }

    if (!emailForSheets) {
      setLoadingSheets(false);
      setBaseSheetNames([]);
      setMembershipDenied(false);
      setMembershipDeniedText('');
      setError(null);
      return;
    }

    // The brand is part of the key: two brands offer different strategies, and
    // one brand's cached list must never be served to the other. (Switching
    // brands also clears these keys and reloads — this is the belt to that
    // braces, and covers a second tab left open on another brand.)
    const entityForSheets = getActiveEntitySlug() || 'vector';
    const audienceKey = sheetsAudienceCacheKey(isAdminActual, adminViewAs);
    // v4: audience in the key — v3 reused admin elevated lists when previewing Basic/Pro/Max.
    // v5: visibility moved to Sheet Manager's own column, so a v4 entry can hold
    // a sheet list and an `internalSheets` array from the old shared value. Both
    // the hit path and the 429 back-off path return early on a cache hit, which
    // would serve the previous visibility — and blank every internal marker —
    // until the TTL expired.
    const cacheKey = `risklo_sheet_names_cache_v6_${entityForSheets}_${emailForSheets}_${audienceKey}`;
    const cooldownKey = `risklo_sheet_names_retry_after_v2_${entityForSheets}_${emailForSheets}`;
    const now = Date.now();

    const safelyParseJson = (raw) => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (_err) {
        return null;
      }
    };

    // Use recent local cache on refresh to avoid unnecessary /api/sheets calls.
    const cached = safelyParseJson(localStorage.getItem(cacheKey));
    if (
      cached &&
      cached.noVectorAccess &&
      !isAdmin &&
      Number.isFinite(cached.cachedAt) &&
      now - cached.cachedAt < SHEET_NAMES_NO_VECTOR_CACHE_TTL_MS
    ) {
      setBaseSheetNames([SAMPLE_STRATEGY_NAME]);
      setMembershipDenied(true);
      setMembershipDeniedText('');
      setError(null);
      setLoadingSheets(false);
      return;
    }
    if (
      cached &&
      Array.isArray(cached.sheets) &&
      cached.sheets.length > 0 &&
      Number.isFinite(cached.cachedAt) &&
      now - cached.cachedAt < SHEET_NAMES_CACHE_TTL_MS
    ) {
      setBaseSheetNames(cached.sheets);
      setInternalSheetNames(Array.isArray(cached.internalSheets) ? cached.internalSheets : []);
      const labelMap = cached.sheetLabels && typeof cached.sheetLabels === 'object' ? cached.sheetLabels : {};
      setSheetLabels(labelMap);
      setStrategyLabels(labelMap);
      setError(null);
      setLoadingSheets(false);
      return;
    }

    // After a 429, back off briefly to avoid repeated refresh hammering.
    const retryAfterTs = Number(localStorage.getItem(cooldownKey) || 0);
    if (Number.isFinite(retryAfterTs) && retryAfterTs > now) {
      if (cached && Array.isArray(cached.sheets) && cached.sheets.length > 0) {
        setBaseSheetNames(cached.sheets);
        setInternalSheetNames(Array.isArray(cached.internalSheets) ? cached.internalSheets : []);
        const labelMap = cached.sheetLabels && typeof cached.sheetLabels === 'object' ? cached.sheetLabels : {};
        setSheetLabels(labelMap);
        setStrategyLabels(labelMap);
        setError(null);
      } else {
        setError('Too many requests. Please wait a moment before refreshing again.');
      }
      setLoadingSheets(false);
      return;
    }

    setLoadingSheets(true);

    try {
      const url = `${API_ENDPOINTS.sheets}?email=${encodeURIComponent(emailForSheets)}`;
      // Headers matter here: without them the request carries no
      // X-RiskLo-Entity, so the server falls back to Vector and every brand
      // gets Vector's strategy tabs.
      const response = await fetch(url, {
        headers: getVectorApiHeaders({ isAdminActual, adminViewAs }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 429) {
          localStorage.setItem(cooldownKey, String(now + SHEET_NAMES_429_COOLDOWN_MS));
        }
        if (vectorSheetAccessDenied(response, data)) {
          setBaseSheetNames([SAMPLE_STRATEGY_NAME]);
          setMembershipDenied(true);
          setMembershipDeniedText(parseMembershipDeniedMessage(data));
          setError(null);
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ noVectorAccess: true, cachedAt: now })
          );
          localStorage.removeItem(cooldownKey);
          setLoadingSheets(false);
          return;
        }
        throw new Error(data?.error || `Server returned ${response.status}: ${response.statusText}`);
      }

      if (data.success) {
        // A successful list means access is fine — clear any earlier denial
        // (e.g. a membership that has since been granted, or a brand switch).
        setMembershipDenied(false);
        setMembershipDeniedText('');
        // Filter out non-strategy sheets from strategy dropdowns.
        const DATE_SHEET_PATTERN = /^\d{1,2}\.\d{4}$/;
        const filtered = (data.sheets || []).filter((name) => {
          const normalized = (name || '').trim().toLowerCase();
          if (!normalized) return false;
          if (DATE_SHEET_PATTERN.test(name)) return false;
          if (normalized.includes('current results')) return false;
          if (normalized.includes('past results')) return false;
          if (normalized === 'summary') return false;
          return true;
        });
        // Always inject the virtual "Vector Master Strat" entry. Its composition
        // is now admin-editable and stored server-side (with a legacy hardcoded
        // fallback), so the server can always build it regardless of whether the
        // original hardcoded source-sheet names are still present. (Previously
        // this was gated on all three hardcoded sources existing, which broke
        // when Vector renamed a source sheet — the entry vanished everywhere.)
        // Prepend so it shows at the top of every strategy dropdown.
        // Strip both the current and legacy "Multi-Strat" names so the
        // injection below adds exactly one entry under the canonical name.
        const multiStratNames = new Set([MULTI_STRAT_SHEET_NAME, ...LEGACY_MULTI_STRAT_NAMES]);
        const withoutMultiStrat = filtered.filter((s) => !multiStratNames.has(s));
        const serverListedMasterStrat = filtered.some((s) => multiStratNames.has(s));
        const hasRealStrategies = withoutMultiStrat.some((s) => s !== SAMPLE_STRATEGY_NAME);
        const isLegacySheetListApi = data.hasMasterStrat === undefined;
        const hasMasterStratComposition = isLegacySheetListApi
          ? hasRealStrategies
          : Boolean(data.hasMasterStrat);
        // Modern /api/sheets applies Sheet Manager visibility and ordering, so a
        // hidden Master Strat is intentionally absent from `filtered`. Only
        // legacy responses (no hasMasterStrat field) still need client injection.
        const withMultiStrat = isLegacySheetListApi
          && !serverListedMasterStrat
          && hasMasterStratComposition
          && hasRealStrategies
          ? [MULTI_STRAT_SHEET_NAME, ...withoutMultiStrat]
          : filtered;
        setBaseSheetNames(withMultiStrat);
        setInternalSheetNames(Array.isArray(data.internalSheets) ? data.internalSheets : []);
        const labelMap = data.sheetLabels && typeof data.sheetLabels === 'object' ? data.sheetLabels : {};
        setSheetLabels(labelMap);
        // Publish to the module store as well, so resolveStrategyDisplayName can
        // apply renames anywhere without this prop reaching that component.
        setStrategyLabels(labelMap);
        // Cache the post-injection list so the next pageload's instant render
        // already includes Multi-Strat when the sources are present.
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            sheets: withMultiStrat,
            internalSheets: Array.isArray(data.internalSheets) ? data.internalSheets : [],
            sheetLabels: labelMap,
            cachedAt: now,
          })
        );
        localStorage.removeItem(cooldownKey);
        if (filtered.length === 0) {
          setError('No strategies found.');
        } else {
          setError(null);
        }
      } else {
        setError(data.error || 'Failed to load strategies');
      }
    } catch (err) {
      console.error('Failed to fetch sheet names:', err);
      console.error('Hostname:', window.location.hostname);

      if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
        setError('RiskLo is temporarily unavailable. We\'re working on it and will be back online shortly.');
      } else {
        setError(err.message || 'RiskLo is temporarily unavailable. We\'re working on it and will be back online shortly.');
      }
      } finally {
        setLoadingSheets(false);
      }
  }, [
    authLoading,
    user?.email,
    isAdmin,
    isAdminActual,
    adminViewAs,
    SHEET_NAMES_CACHE_TTL_MS,
    SHEET_NAMES_NO_VECTOR_CACHE_TTL_MS,
    SHEET_NAMES_429_COOLDOWN_MS,
  ]);

  // Arrange tabs (Results page) saved: drop this browser's cached tab lists and
  // reload, so the new order / visibility shows without a refresh.
  useEffect(() => {
    const onSheetsChanged = () => {
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith('risklo_sheet_names_cache_'))
          .forEach((k) => localStorage.removeItem(k));
      } catch (_e) { /* storage unavailable: the fetch below still runs */ }
      fetchSheetNames();
    };
    window.addEventListener(SHEETS_CHANGED_EVENT, onSheetsChanged);
    return () => window.removeEventListener(SHEETS_CHANGED_EVENT, onSheetsChanged);
  }, [fetchSheetNames]);

  // Fetch sheet names on mount and when signed-in user changes
  useEffect(() => {
    // Don't fetch while auth is still loading
    if (authLoading) return;
    fetchSheetNames();
  }, [authLoading, fetchSheetNames]);

  // Handle sign-in OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const signIn = params.get('signIn');
    const userInfoStr = params.get('userInfo');
    const signInEmail = params.get('email');
    const signInReason = params.get('reason');

    const clearOAuthQueryParams = () => {
      setTimeout(() => {
        window.history.replaceState({}, '', window.location.pathname);
      }, 1000);
    };

    if (signIn === 'error') {
      console.error('OAuth sign-in error:', signInReason || 'unknown');
      setShowDisclaimer(false);
      setCurrentPage('login');
      clearOAuthQueryParams();
      return;
    }

    if (signIn === 'success' && !pickerShownRef.current) {
      console.log('🔐 OAuth callback: Sign-in flow detected');
      pickerShownRef.current = true; // prevent duplicate
      setShowDisclaimer(false);
      (async () => {
        try {
          let userInfo = null;
          if (userInfoStr) {
            userInfo = JSON.parse(decodeURIComponent(userInfoStr));
          } else if (signInEmail) {
            userInfo = { email: signInEmail, name: signInEmail.split('@')[0], provider: 'google' };
          }
          if (userInfo?.email) {
            const ok = await handleGoogleSignIn(userInfo);
            if (ok) {
              navigate('/dashboard', { replace: true });
            } else {
              setCurrentPage('login');
            }
          } else {
            setCurrentPage('login');
          }
        } catch (err) {
          console.error('Error parsing user info from OAuth callback:', err);
          setCurrentPage('login');
        } finally {
          clearOAuthQueryParams();
        }
      })();
      return;
    }
  }, [handleGoogleSignIn, navigate, setCurrentPage]);


  const handleAnalyze = async (formData) => {
    setLoading(true);
    trackEvent('run_analysis_clicked', {
      source: 'blowout_or_30_page',
      mode: riskMode,
      has_user: Boolean(user?.email),
    });

    setError(null);

    setLastFormData(formData); // Store form data for calculation details
    try {
      const stored = JSON.parse(localStorage.getItem(FORM_INPUTS_STORAGE_KEY) || '{}');
      stored[riskMode] = formData;
      localStorage.setItem(FORM_INPUTS_STORAGE_KEY, JSON.stringify(stored));
    } catch (_err) {}
    
    try {
      const response = await fetch(API_ENDPOINTS.analyze, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getBearerAuthHeaders(),
        },
        body: JSON.stringify({
          ...formData,
          userEmail: user?.email || null,
          consistencyPercent: formData.consistencyPercent || consistencyPercent,
          startOfDayProfit: formData.startOfDayProfit || null,
          safetyNet: formData.safetyNet || null,
          profitSinceLastPayout: formData.profitSinceLastPayout || null
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to analyze data');
      }

      // Store metrics based on current mode
      if (riskMode === 'risk') {
        setRiskMetrics(data.metrics);
      } else {
        setApexMaeMetrics(data.metrics);
      }

      // Scroll results into view after a short delay so DOM updates
      setTimeout(() => {
        document.getElementById('risk-results-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    } catch (err) {
      setError(err.message);
      // Clear metrics for current mode on error
      if (riskMode === 'risk') {
        setRiskMetrics(null);
      } else {
        setApexMaeMetrics(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetAnalyzeInputs = useCallback(() => {
    setLastFormData(null);
    if (riskMode === 'risk') {
      setRiskMetrics(null);
    } else {
      setApexMaeMetrics(null);
    }
    try {
      const stored = JSON.parse(localStorage.getItem(FORM_INPUTS_STORAGE_KEY) || '{}');
      delete stored[riskMode];
      localStorage.setItem(FORM_INPUTS_STORAGE_KEY, JSON.stringify(stored));
    } catch (_err) {}
  }, [riskMode]);

  // Clear metrics when switching modes
  const handleModeChange = (newMode) => {
    setRiskMode(newMode);
    setCurrentPage('home');
    // Don't clear metrics - let user see they need to analyze for the new mode
  };

  const renderPage = () => {
    // Symbol pages are path-addressed (/symbol/NVDA) so a row click produces a
    // shareable URL and the browser back button works, rather than a modal.
    if (currentPage.startsWith('symbol/')) {
      const sym = decodeURIComponent(currentPage.slice('symbol/'.length)).toUpperCase();
      return <SymbolDetailPage symbol={sym} onBack={() => navigate(-1)} />;
    }
    // Users → Feature access can hide a page per tier. Switches only hide: each
    // page still applies its own tier rules (Pro locks, staff-only, etc.).
    const pageFeature = featureForPage(currentPage, riskMode);
    if (pageFeature && !canSeeFeature(pageFeature)) {
      return (
        <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
          <p>This page isn&apos;t available yet.</p>
          <button
            onClick={() => setCurrentPage('dashboard')}
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
          >
            Back to Overview
          </button>
        </div>
      );
    }
    // Admin → Brands → Features: a feature this brand hasn't unlocked shows a
    // preview asking the member to speak to their brand owner.
    if (pageFeature && isFeatureLocked(pageFeature)) {
      return (
        <LockedFeaturePreview
          featureKey={pageFeature}
          brandName={featureBrand?.displayName}
          onBack={() => navigate(-1)}
        />
      );
    }
    switch (currentPage) {
      case 'about':
        return <AboutPage onNavigate={setCurrentPage} user={user} />;
      case 'updates':
        return <VectorUpdateLog onNavigate={setCurrentPage} user={user} />;
      case 'features':
        return <FeaturesPage onNavigate={setCurrentPage} user={user} />;
      case 'companies':
        return <CompaniesPage onNavigate={setCurrentPage} />;
      case 'pricing':
        return (
          <PricingPage
            onNavigate={setCurrentPage}
            user={user}
          />
        );
      case 'contact':
        return <ContactPage onNavigate={setCurrentPage} />;
      case 'help':
        return <HelpPage onNavigate={setCurrentPage} user={user} />;
      case 'how-we-calculate':
        return <HowWeCalculate onNavigate={setCurrentPage} />;
      case 'faq':
        return <FAQ onNavigate={setCurrentPage} />;
      case 'apex-30-percent-rule':
      case 'consistency-rules':
        return <Apex30PercentRule onNavigate={setCurrentPage} />;
      case 'how-to-export-csv':
        return <HowToExportCsv onNavigate={setCurrentPage} />;
      case 'desktopAppGuide':
        return <DesktopAppGuide onNavigate={setCurrentPage} />;
      case 'account':
        return <Account onNavigate={setCurrentPage} onUpgrade={openUpgradeModal} />;
      case 'terms-and-conditions':
        return <TermsAndConditions onNavigate={setCurrentPage} />;
      case 'privacy-policy':
        return <PrivacyPolicy onNavigate={setCurrentPage} />;
      case 'security':
        return <Security onNavigate={setCurrentPage} />;
      case 'tutorials':
        return <Tutorials onNavigate={setCurrentPage} showTour={canSeeIntroTour} />;
      case 'login':
        return <Login onNavigate={setCurrentPage} theme={theme} onToggleTheme={toggleTheme} />;
      case 'brand-onboarding':
        // Anyone signed in with an invite link; the API checks the link.
        return <BrandOnboarding onNavigate={setCurrentPage} />;
      case 'admin-feedback':
        return isAdmin ? (
          <AdminFeedback onNavigate={setCurrentPage} />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Admin only.</p>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        );
      case 'admin-vector-nt-mappings':
        return isAdmin ? (
          <AdminVectorNtMappings onNavigate={setCurrentPage} />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Admin only.</p>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        );
      case 'brand-settings':
        return (isAdmin || isVectorAdmin) ? <BrandSettings /> : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Brand Admin access required.</p>
          </div>
        );
      case 'brand-strategies':
        return (isAdmin || isVectorAdmin) ? (
          <BrandStrategies onNavigate={setCurrentPage} />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Brand Admin access required.</p>
          </div>
        );
      case 'admin-strategy-submission-review':
        return isAdmin ? (
          <AdminStrategySubmissionReview />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Full Admin only.</p>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        );
      case 'admin-scenario-data':
        return (isAdmin || isVectorAdmin) ? (
          <AdminScenarioData onNavigate={setCurrentPage} />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Admin or Brand Admin only.</p>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        );
      case 'admin-entities':
        // Full admin only: entities span every brand, so entity-scoped staff
        // (Vector Admin) must not reach it. The API enforces this as well.
        return isAdmin ? (
          <AdminEntities onNavigate={setCurrentPage} />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Full Admin only.</p>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        );
      case 'admin-users':
        return (isAdmin || isVectorAdmin) ? (
          <AdminUsers onNavigate={setCurrentPage} />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Admin or Brand Admin only.</p>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        );
      case 'trade-watcher':
        // Admin and Support see every client; Pro (and up) see only their own
        // accounts. The server enforces the same rules on every request.
        // Shown per tier via Users → Feature access (Full Admin always), and
        // Pro and up to use. Inside, Admin/Support see every client; others
        // only their own.
        if (canSeeTradeWatcher && !isAdmin && !isPro) {
          return <TradeWatcherProLock onUpgrade={openUpgradeModal} />;
        }
        return canSeeTradeWatcher ? (
          <TradeWatcher onNavigate={setCurrentPage} />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Trade Watcher isn&apos;t available yet.</p>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        );
      case 'admin-alerts':
        return isAdmin ? (
          <AdminAlerts onNavigate={setCurrentPage} />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Full Admin only.</p>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        );
      case 'admin-site-metrics':
        return isAdminActual ? (
          <AdminSiteMetrics />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Full Admin only.</p>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        );
      case 'admin-metrics-history':
        return (isAdmin || isVectorAdmin) ? (
          <AdminMetricsHistory onNavigate={setCurrentPage} />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Admin or Brand Admin only.</p>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        );
      case 'admin-ai':
        return isAdminActual ? (
          <AdminAiAssistant onNavigate={setCurrentPage} />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Access denied. Full Admin only.</p>
          </div>
        );
      case 'admin-logs':
        return (isAdmin || isVectorAdmin) ? (
          <AdminLogs onNavigate={setCurrentPage} />
        ) : (
          <div className="admin-access-denied" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <p>Access denied. Admin or Brand Admin only.</p>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
            >
              Back to Dashboard
            </button>
          </div>
        );
      case 'results':
        if (user?.email && workspace.loading) return null;
        // Results is hidden for self users for now (see the redirect above).
        if (personalViews) return null;
        return (
          <ResultsDashboard
            view="vector"
            onNavigate={setCurrentPage}
            isMax={isMax}
            isAdmin={isAdmin}
            authLoading={authLoading}
            tierReady={tierReady}
            user={user}
            isPro={isPro}
            showBetaEndMessage={showBetaEndMessage}
            onUpgrade={openUpgradeModal}
            sheetNames={sheetNames}
            internalSheetNames={internalSheetNames}
            sheetLabels={sheetLabels}
            loadingSheets={loadingSheets}
            error={error}
            customCombos={customCombos}
            customCombosLoaded={customCombosLoaded}
            onCustomComboUpsert={upsertCustomCombo}
            onCustomComboRemove={removeCustomCombo}
            isAdminActual={isAdminActual}
            adminViewAs={adminViewAs}
          />
        );
      case 'my-results':
        return (
          <ResultsDashboard
            view="ninjatrader"
            onNavigate={setCurrentPage}
            isMax={isMax}
            isAdmin={isAdmin}
            isVectorAdminPlus={isVectorAdminPlus}
            authLoading={authLoading}
            tierReady={tierReady}
            user={user}
            isPro={isPro}
            showBetaEndMessage={showBetaEndMessage}
            onUpgrade={openUpgradeModal}
            sheetNames={sheetNames}
            loadingSheets={loadingSheets}
            error={error}
          />
        );
      case 'client-reporting':
        return (
          <VectorDashboardPage
            user={user}
            isAdmin={isAdmin}
            isVectorAdmin={isVectorAdmin}
            isMax={isMax}
            isPro={isPro}
            isBeta={isBeta}
            tierReady={tierReady}
            authLoading={authLoading}
            onUpgrade={openUpgradeModal}
            onNavigate={setCurrentPage}
          />
        );
      case 'vector-capital':
        return (
          <VectorCapitalPage
            user={user}
            isAdmin={isAdmin}
            authLoading={authLoading}
            tierReady={tierReady}
            onNavigate={setCurrentPage}
          />
        );
      case 'vector-capital-plus':
        return (
          <VectorCapitalPlusPage
            user={user}
            isAdmin={isAdmin}
            authLoading={authLoading}
            tierReady={tierReady}
            onNavigate={setCurrentPage}
          />
        );
      case 'risk-tools':
        return (
          <RiskToolsPage
            isPro={isPro}
            canSeeFeature={canSeeFeature}
            isFeatureLocked={isFeatureLocked}
            onNavigate={setCurrentPage}
            onModeChange={handleModeChange}
          />
        );
      case 'position-sizer':
      case 'concentration-risk':
      case 'drawdown-risk':
      case 'stop-planner':
      case 'rebalance':
        return <PortfolioRiskPage tool={currentPage} isPro={isPro} onNavigate={setCurrentPage} onUpgrade={openUpgradeModal} />;
      case 'prop-firm-planner':
        return <PropFirmPlannerPage user={user} isPro={isPro} onUpgrade={openUpgradeModal} />;
      case 'hedging-risk':
        return (
          <HedgingRiskPage
            user={user}
            isPro={isPro}
            authLoading={authLoading}
            tierReady={tierReady}
            showBetaEndMessage={showBetaEndMessage}
            onNavigate={setCurrentPage}
            onUpgrade={openUpgradeModal}
          />
        );
      case 'portfolio-simulator':
        if (user?.email && workspace.loading) return null;
        if (personalViews) {
          return <MySimulatorPage user={user} isPro={isPro} onNavigate={setCurrentPage} onUpgrade={openUpgradeModal} />;
        }
        return (
          <PortfolioSimulatorPage
            user={user}
            isPro={isPro}
            authLoading={authLoading}
            tierReady={tierReady}
            hasVectorAccess={hasVectorAccess}
            hasStrategyData={hasStrategyData}
            loadingSheets={loadingSheets}
            membershipDeniedText={shownMembershipDeniedText}
            showBetaEndMessage={showBetaEndMessage}
            onNavigate={setCurrentPage}
            onUpgrade={openUpgradeModal}
          />
        );
      case 'results-spreadsheet':
        return (
          <ResultsSpreadsheetPage
            user={user}
            isPro={isPro}
            onUpgrade={openUpgradeModal}
          />
        );
      case 'dashboard':
        return (
          <DashboardPage
            user={user}
            isPro={isPro}
            isMax={isMax}
            isVectorAdmin={isVectorAdmin}
            isAdmin={isAdmin}
            tierReady={tierReady}
            sheetNames={sheetNames}
            customCombos={customCombos}
            onCustomComboUpsert={upsertCustomCombo}
            loadingSheets={loadingSheets}
            hasVectorAccess={hasVectorAccess}
            membershipDeniedText={shownMembershipDeniedText}
            onNavigate={setCurrentPage}
            onUpgrade={openUpgradeModal}
          />
        );
      case 'home':
      default:
        if (vanityChecking) return null;
        if (!user && !authLoading) {
          return (
            <LandingPage
              onNavigate={setCurrentPage}
              user={user}
            />
          );
        }
        return (
          <>
            <PageHeading
              title={riskMode === 'apexMae' ? '% Rule Risk' : 'Blowout Risk'}
              description={riskMode === 'apexMae'
                ? 'Check trades against your prop firm’s consistency / % rule.'
                : 'Estimate the risk of breaching your account drawdown.'}
            />

            {error && !authLoading && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Unified Tabs - All three options visible */}
            <div className="risk-assessment-tabs">
              <button 
                className={`risk-tab ${activeTab === 'single' ? 'active' : ''} ${!isPro ? 'pro-locked' : ''}`}
                onClick={() => setActiveTab('single')}
              >
                {riskMode === 'apexMae' ? '% Rule Risk' : 'Account Blowout Risk'}
                {!isPro && <span className="pro-badge-inline">PRO</span>}
              </button>
              <button 
                className={`risk-tab ${activeTab === 'bulk' ? 'active' : ''} ${!isPro ? 'pro-locked' : ''}`}
                onClick={() => setActiveTab('bulk')}
              >
                Bulk Risk
                {!isPro && <span className="pro-badge-inline">PRO</span>}
              </button>
              <button 
                className={`risk-tab ${activeTab === 'csv' ? 'active' : ''} ${!isPro ? 'pro-locked' : ''}`}
                onClick={() => setActiveTab('csv')}
              >
                CSV Upload
                {!isPro && <span className="pro-badge-inline">PRO</span>}
              </button>
            </div>

            {/* Single Strategy Tab */}
            {activeTab === 'single' && (
              <>
                {showBetaEndMessage ? (
                  <div className="tab-content" style={{ position: 'relative', minHeight: '400px' }}>
                    <RiskAssessmentProLock
                      riskMode={riskMode}
                      signedIn={Boolean(user?.email)}
                      showBetaEndMessage
                      onUpgrade={openUpgradeModal}
                      onNavigate={setCurrentPage}
                    />
                  </div>
                ) : !isPro ? (
                  <RiskAssessmentProLock
                    riskMode={riskMode}
                    signedIn={Boolean(user?.email)}
                    onUpgrade={openUpgradeModal}
                    onNavigate={setCurrentPage}
                  />
                ) : (
                  <>
                <InputForm
                  onSubmit={handleAnalyze}
                  consistencyPercent={consistencyPercent}
                  onConsistencyPercentChange={setConsistencyPercent}
                  initialFormData={lastFormData}
                  showResetButton={Boolean(lastFormData)}
                  onReset={handleResetAnalyzeInputs}
                  loading={loading}
                  sheetNames={sheetNames}
                  customCombos={customCombos}
                  loadingSheets={loadingSheets}
                  error={!authLoading && error && sheetNames.length === 0 ? error : null}
                  riskMode={riskMode}
                  onNavigate={setCurrentPage}
                />

                <div id="risk-results-section" style={{ scrollMarginTop: '120px' }}>
                {metrics ? (
                  <Dashboard metrics={metrics} riskMode={riskMode} onNavigate={setCurrentPage} formData={lastFormData} consistencyPercent={consistencyPercent} />
                ) : (
                  <div className="no-results-message">
                    <div className="no-results-content">
                      <div className="no-results-icon"><IconChart size={64} /></div>
                      <h3 className="no-results-title">
                        {riskMode === 'risk' ? 'Risk Results' : '% Rule Results'}
                      </h3>
                      <p className="no-results-text">
                        {riskMode === 'risk' 
                          ? 'Analyze your strategy to see risk assessment results.'
                          : 'Analyze your strategy to see % Rule results.'}
                      </p>
                    </div>
                  </div>
                )}
                </div>
                  </>
                )}
              </>
            )}

            {/* Bulk Risk Tab */}
            {activeTab === 'bulk' && (
              <div className="tab-content">
                {bulkInputRows !== null ? (
                  <BulkRiskCalculator
                    isPro={isPro}
                    showBetaEndMessage={showBetaEndMessage}
                    sheetNames={sheetNames}
                    customCombos={customCombos}
                    riskMode={riskMode}
                    consistencyPercent={consistencyPercent}
                    onConsistencyPercentChange={setConsistencyPercent}
                    onNavigate={setCurrentPage}
                    onUpgrade={openUpgradeModal}
                    onManageEmailSummaries={goToAccountEmailSummaries}
                    csvBulkRows={csvBulkRows}
                    csvFileNames={csvFileNames}
                    onClearCsvData={() => {
                      setCsvBulkRows(null);
                      setCsvFileNames(null);
                    }}
                    bulkRows={bulkInputRows}
                    setBulkRows={setBulkInputRows}
                    bulkResultsRisk={bulkResultsRisk}
                    bulkResultsApexMae={bulkResultsApexMae}
                    setBulkResultsRisk={setBulkResultsRisk}
                    setBulkResultsApexMae={setBulkResultsApexMae}
                    onResetBulkToDefault={resetBulkToDefault}
                  />
                ) : (
                  <div
                    className="bulk-calculator"
                    style={{
                      minHeight: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,0.55)',
                      fontSize: '0.95rem',
                    }}
                  >
                    Loading bulk calculator…
                  </div>
                )}
              </div>
            )}

            {/* CSV Upload Tab */}
            {activeTab === 'csv' && (
              <div className="tab-content">
                <CsvUpload 
                  isPro={isPro}
                  showBetaEndMessage={showBetaEndMessage}
                  sheetNames={sheetNames}
                  riskMode={riskMode}
                  consistencyPercent={consistencyPercent}
                  onConsistencyPercentChange={setConsistencyPercent}
                  onNavigate={setCurrentPage}
                  onUpgrade={openUpgradeModal}
                  onPopulateBulkRows={(rows, csvFileNames) => {
                    console.log('App.js: Storing CSV data in state', { rowCount: rows.length, csvFileNames });
                    setCsvBulkRows(rows);
                    setCsvFileNames(csvFileNames);
                    // Switch to bulk tab after storing data
                    setActiveTab('bulk');
                  }}
                  onSwitchToBulkTab={() => {
                    // Switch to bulk tab after successful parse
                    setActiveTab('bulk');
                  }}
                />
              </div>
            )}
          </>
        );
    }
  };

  const handleDisclaimerAccept = () => {
    const email = user?.email && String(user.email).trim();
    if (!email) return;
    acceptDisclaimer(email);
    setShowDisclaimer(false);
  };


  // Beta launch is live — Basic users see normal upgrade prompts, not beta-ended lockout.
  // Admins previewing Basic/Pro (isAdmin false in UI) must never hit this gate.
  const showBetaEndMessage = false;

  const siteStatusBroadcastMessage = getSiteStatusBroadcastMessage();

  const showMyPortfolioNav = tierReady && Boolean(user);
  const hasMyPortfolioAccess = hasClientReportingAccess({
    isSignedIn: Boolean(user),
    isPro,
    isMax,
    isBeta,
    isAdmin,
    isVectorAdmin,
  });

  // Single global header for every page except login. Pages that previously rendered
  // their own simple header (About, FAQ, Account, Vector Capital, …) now sit under the
  // standard app-header so the RiskLo brand + sidebar trigger are consistent everywhere.
  // The legacy <PageHeader /> / <VectorCapitalPageHeader /> elements are hidden via CSS
  // (.page-header-simple { display: none }) — the components are still imported so we
  // don't have to touch every page file.
  const showMainHeader = currentPage !== 'login';
  const mobileHeaderHidden = useMobileHeaderAutoHide({
    enabled: showMainHeader,
    resetKey: currentPage,
  });

  // Marketing layout (public funnel): landing home when signed out, plus select pages.
  // `/about` and `/faq` use the in-app shell (sidebar) so signed-in users stay in the product.
  if (isMarketingRoute) {
    return (
      <div className="App">
        <MarketingLayout
          user={user}
          currentPage={currentPage}
          onNavigate={setCurrentPage}
          onPrimaryCta={() => {
            if (!user) {
              setCurrentPage('login');
              return;
            }
            // While tier is resolving, avoid flashing the upgrade modal for Pro/Max users.
            if (!tierReady) {
              setCurrentPage(lastVisitedTab === 'results' ? 'results' : 'home');
              return;
            }
            if (isPro || isMax || isAdmin || isBeta) {
              setCurrentPage(lastVisitedTab === 'results' ? 'results' : 'home');
              return;
            }
            setUpgradeModalMessage(null);
            setShowUpgradeModal(true);
          }}
        >
          {renderPage()}
        </MarketingLayout>
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            setUpgradeModalMessage(null);
          }}
          user={user}
          isPro={isPro}
          customMessage={upgradeModalMessage}
        />
      </div>
    );
  }

  return (
    <div className={`App ${currentPage !== 'login' ? 'has-sidebar' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <TraderPsychologyTipBanner />

      {currentPage !== 'login' && (
        <Sidebar
          currentPage={currentPage}
          riskMode={riskMode}
          onNavigate={setCurrentPage}
          onHome={goHome}
          onModeChange={(mode) => {
            setCurrentPage('home');
            handleModeChange(mode);
          }}
          onUpgrade={openUpgradeModal}
          isPro={isPro}
          isMax={isMax}
          isVectorAdmin={isVectorAdmin}
          isBeta={isBeta}
          isDevMode={isDevMode}
          isAdmin={isAdmin}
          isVectorAdminPlus={isVectorAdminPlus}
          isSupport={isSupport}
          isSales={isSales}
          canSeeTradeWatcher={canSeeTradeWatcher}
          canSeeFeature={canSeeFeature}
          isFeatureLocked={isFeatureLocked}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={toggleSidebarCollapsed}
          theme={theme}
          onToggleTheme={toggleTheme}
        />
      )}

      {showDisclaimer && user?.email && (
        <DisclaimerModal onAccept={handleDisclaimerAccept} />
      )}

      <TutorialModal
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
      />

      <GuidedTour
        isOpen={showGuidedTour && canSeeIntroTour && Boolean(user?.email) && currentPage !== 'login'}
        onClose={() => setShowGuidedTour(false)}
        user={user}
      />

      {showMainHeader && (
        <header className={`app-header${mobileHeaderHidden ? ' is-scroll-hidden' : ''}`}>
          <div className="header-content">
            <div className="header-left">
              {currentPage !== 'login' && (
                <button
                  type="button"
                  className="mobile-sidebar-trigger"
                  onClick={() => setIsSidebarOpen(true)}
                  aria-label="Open menu"
                  aria-expanded={isSidebarOpen}
                >
                  <span className="mobile-sidebar-trigger-icon" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                </button>
              )}
              {currentPage !== 'login' && user && (
                <button
                  type="button"
                  className="mobile-account-trigger mobile-avatar-trigger"
                  onClick={() => setCurrentPage('account')}
                  aria-label="Account"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt=""
                      aria-hidden="true"
                      className="mobile-avatar-trigger-img"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="mobile-avatar-trigger-fallback" aria-hidden="true">
                      {(() => {
                        const src = user?.name || user?.email || '';
                        const parts = src.split(/[\s@.]+/).filter(Boolean);
                        if (parts.length === 0) return 'U';
                        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
                        return (parts[0][0] + parts[1][0]).toUpperCase();
                      })()}
                    </span>
                  )}
                </button>
              )}
              {currentPage !== 'login' && !user && (
                <div className="header-sign-in-mobile">
                  <GoogleSignIn onNavigate={setCurrentPage} />
                </div>
              )}
              <div className="title-with-badge" style={{ cursor: 'pointer' }} onClick={goHome}>
                <h1 className="app-title">{brandIdentity.appName}</h1>
                {/* Tier priority: Admin > Brand Admin > Max > Beta > Pro > Basic.
                    Mirrors Sidebar.js so desktop and mobile show the same
                    label/color for the user's effective tier. */}
                <span
                  className={`header-badge ${
                    !tierReady && user
                      ? 'basic-badge-header'
                      : isAdmin
                        ? 'admin-badge-header'
                        : isVectorAdmin
                          ? 'vadmin-badge-header'
                          : isMax
                            ? 'max-badge-header'
                            : isBeta
                              ? 'beta-badge-header'
                              : isPro
                                ? 'pro-badge-header'
                                : 'basic-badge-header'
                  }`}
                  aria-busy={Boolean(user && !tierReady)}
                >
                  {!tierReady && user
                    ? '···'
                    : isAdmin
                      ? 'Admin'
                      : isVectorAdminPlus
                        ? 'Brand Admin+'
                        : isVectorAdmin
                        ? 'Brand Admin'
                        : isMax
                          ? 'Max'
                          : isBeta
                            ? 'Beta'
                            : isPro
                              ? 'Pro'
                              : 'Basic'}
                </span>
              </div>
              <p className="app-subtitle">{brandIdentity.isPlatform ? 'Trading Risk Platform' : (brandIdentity.tagline || 'Powered by RiskLo')}</p>
              {isAdminViewPreview && (
                <p className="admin-view-preview-hint">
                  Previewing as {adminViewLabel(adminViewAs)}
                  {' · '}
                  <button type="button" className="admin-view-preview-link" onClick={() => setCurrentPage('account')}>
                    Switch on Account
                  </button>
                </p>
              )}
            </div>
            <div className="header-center">
              {currentPage !== 'login' && (
              <div className="mode-toggle-container">
                <button
                  className={`mode-toggle-btn ${currentPage === 'home' && riskMode === 'risk' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentPage('home');
                    handleModeChange('risk');
                  }}
                >
                  Blowout Risk
                </button>
                <button
                  className={`mode-toggle-btn ${currentPage === 'home' && riskMode === 'apexMae' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentPage('home');
                    handleModeChange('apexMae');
                  }}
                >
                  % Rule Risk
                </button>
                <button
                  className={`mode-toggle-btn ${currentPage === 'results' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentPage('results');
                  }}
                >
                  Results
                </button>
              </div>
              )}
            </div>
            <div className="header-right">
              {currentPage !== 'login' && (
                <div className={`header-sign-in-desktop ${user ? 'signed-in' : ''}`}>
                  <GoogleSignIn onNavigate={setCurrentPage} />
                </div>
              )}
            </div>
          </div>
      </header>
      )}

      <main className="app-main">
        <div className="container">
          {deployUpdateReady && (
            <div className="deploy-update-banner" role="status" aria-live="polite">
              <span className="deploy-update-banner-text">
                A new version of RiskLo is available. Updating automatically in a few seconds…
              </span>
              <button type="button" className="deploy-update-banner-btn" onClick={reloadForDeploy}>
                Update now
              </button>
            </div>
          )}
          {siteStatusBroadcastMessage && (
            <div
              className="site-status-banner"
              role="status"
              aria-live="polite"
            >
              <span className="site-status-banner-icon" aria-hidden="true">
                <IconAlert size={22} color="currentColor" />
              </span>
              <span className="site-status-banner-text">{siteStatusBroadcastMessage}</span>
            </div>
          )}
          <BrokerKeyRotationBanner signedIn={Boolean(user)} currentPage={currentPage} onNavigate={setCurrentPage} />
          {renderPage()}
        </div>
      </main>

      {currentPage !== 'login' && <Footer />}
      {currentPage !== 'login' && (
        <MobileBottomNav
          currentPage={currentPage}
          riskMode={riskMode}
          isPro={isPro}
          showMyPortfolio={showMyPortfolioNav}
          showMyPortfolioProBadge={Boolean(user) && !hasMyPortfolioAccess}
          onNavigate={setCurrentPage}
          onModeChange={handleModeChange}
          canSeeFeature={canSeeFeature}
        />
      )}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          setUpgradeModalMessage(null);
        }}
        user={user}
        isPro={isPro}
        customMessage={upgradeModalMessage}
      />

      {/* RiskLo AI (assistant): on for Pro and up; everyone else sees it locked with an upgrade prompt,
          unless the brand turned it off (then it isn't shown at all). */}
      {user?.email && featureAccessLoaded && currentPage !== 'login' && !isRemovedForBrand('ai_assistant') ? (
        <AskRiskloLauncher
          user={user}
          locked={!canSeeFeature('ai_assistant')}
          lockedReason={isPro ? 'off' : 'pro'}
          onUpgrade={() => openUpgradeModal(`Upgrade to Pro to use ${brandIdentity.poweredBy ? `${brandIdentity.appName} AI` : 'RiskLo AI'}: answers about your own numbers, the market calendar, prop firm rules and every RiskLo tool.`)}
          currentPage={currentPage === 'home' ? (riskMode === 'apexMae' ? 'rule-risk' : 'blowout-risk') : currentPage}
          onOpenPage={(page) => {
            if (page === 'blowout-risk') handleModeChange('risk');
            else if (page === 'rule-risk') handleModeChange('apexMae');
            else setCurrentPage(page);
          }}
        />
      ) : null}

    </div>
  );
}

function App() {
  // Clerk is optional: index.js only mounts ClerkProvider when a publishable key
  // exists, so the bridge (which calls Clerk hooks) must be mounted only then —
  // Clerk hooks throw outside a provider.
  const clerkEnabled = Boolean(process.env.REACT_APP_CLERK_PUBLISHABLE_KEY);
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          {clerkEnabled ? <ClerkSessionBridge /> : null}
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
